import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import OwnerSubHeader from "@/components/OwnerSubHeader";
import { Loader2, RefreshCw, AlertTriangle, FileStack, ChevronRight, FileDown, Mail } from "lucide-react";
import EmailModal from "@/components/EmailModal";
import { downloadReportPdf } from "@/lib/reportPdf";

/**
 * AllReports — the mobile app's "All Reports" screen.
 * Feed comes from GET /api/admin/reports-feed which returns every filed
 * handover/PPF/breakdown/inspection with author + timestamp.
 */
export default function AllReports() {
  const [items, setItems] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState(null);

  const reload = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.get("/admin/reports-feed");
      const arr = Array.isArray(data) ? data : data?.items || data?.reports || data?.data || [];
      setItems(arr);
    } catch (ex) { setErr(apiError(ex)); setItems([]); }
    finally { setBusy(false); }
  };
  useEffect(() => { reload(); }, []);

  return (
    <div className="space-y-5" data-testid="all-reports-page">
      <OwnerSubHeader
        title="All Reports"
        subtitle="Every handover, PPF, breakdown & inspection filed"
        right={
          <button className="btn btn-sm btn-ghost" onClick={reload} disabled={busy}>
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
        }
      />

      {err && <div className="pill pill-red !text-sm !py-2 !px-3 justify-start"><AlertTriangle className="w-3.5 h-3.5" /> {err}</div>}

      {items === null ? (
        <div className="panel-flat p-10 flex items-center justify-center gap-3 text-muted">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading reports feed…
        </div>
      ) : items.length === 0 ? (
        <div className="panel-flat p-10 text-center text-muted">
          <FileStack className="w-8 h-8 mx-auto mb-2 opacity-60" /> No reports yet.
        </div>
      ) : (
        <div className="grid gap-2">
          {items.map((r, i) => {
            const kind = String(r.kind || r.type || "record").toLowerCase();
            const id = r.id || r._id || `${i}`;
            return (
              <div key={id} className="panel p-4 flex items-center gap-3" data-testid={`report-row-${id}`}>
                <span className={`pill ${kindTone(kind)} !text-[10px] !py-0.5 !px-2 uppercase font-black tracking-widest`}>
                  {kind}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-black text-sm truncate">
                    {r.title || r.summary || r.asset_id || r.description || "Report"}
                  </div>
                  <div className="text-[11px] text-muted font-mono-tight mt-0.5 truncate">
                    {r.author_name || r.created_by_name || r.tradesman || "—"} · {fmt(r.created_at || r.updated_at)}
                    {r.site_code && <> · {r.site_code}</>}
                  </div>
                </div>
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => downloadReportPdf(r, kind)}
                  data-testid={`report-pdf-${id}`}
                  title="Download PDF"
                >
                  <FileDown className="w-4 h-4" />
                </button>
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() => setEmail({ kind, item: r })}
                  data-testid={`report-email-${id}`}
                  title="Email"
                >
                  <Mail className="w-4 h-4" />
                </button>
                <ChevronRight className="w-4 h-4 text-muted" />
              </div>
            );
          })}
        </div>
      )}

      {email && (
        <EmailModal
          open={true}
          kind={email.kind}
          item={email.item}
          onClose={() => setEmail(null)}
        />
      )}
    </div>
  );
}

function kindTone(k) {
  if (k.includes("hazard")) return "pill-red";
  if (k.includes("breakdown")) return "pill-orange";
  if (k.includes("ppf")) return "";
  if (k.includes("handover")) return "pill-green";
  return "";
}
function fmt(v) { if (!v) return "—"; try { return new Date(v).toLocaleString(); } catch { return String(v); } }
